/*
 * Elexa Roadmap (sections/roadmap.liquid): <elexa-roadmap> drives the scroll-linked fill line and
 * the step reveals.
 * Fill (softer mid-screen variant): S = section box, vh = viewport height, L = bottom of the last
 * step. Below the viewport -> 0, above it -> 1. Otherwise scrolled = max(0, vh * 0.5 - S.top),
 * progress = clamp(scrolled / ((L - S.top) * 0.8), 0, 1). Updated once per animation frame.
 * Reveal: a step fades up when 20% visible (viewport bottom inset 100 px) and hides again when it
 * leaves, so it re-plays on every pass. Reduced motion and the theme editor: always visible.
 */
(() => {
  if (customElements.get('elexa-roadmap')) return;

  class ElexaRoadmap extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.steps = Array.from(this.querySelectorAll('.ex-rm__step'));
      if (!this.steps.length) return;
      this.initialised = true;
      this.section = this.closest('[data-ex-section]') || this;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const design = Boolean(window.Shopify && window.Shopify.designMode);

      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);

      if (!this.reduce && !design) {
        this.setAttribute('data-reveal', '');
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.target.classList.toggle('is-visible', e.isIntersecting)),
            { threshold: 0.2, rootMargin: '0px 0px -100px 0px' }
          );
          this.steps.forEach((s) => this.io.observe(s));
        } else {
          this.manualReveal = true;
        }
      }
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.io) this.io.disconnect();
      this.initialised = false;
    }

    update() {
      const vh = window.innerHeight;
      const S = this.section.getBoundingClientRect();
      // Below the viewport: empty. Scrolled past (above it): full, so the line never resets and
      // re-animates while the section scrolls back in from the top.
      let progress = S.bottom < 0 ? 1 : 0;
      if (!(S.bottom < 0 || S.top > vh)) {
        const L = this.steps[this.steps.length - 1].getBoundingClientRect().bottom;
        const scrolled = Math.max(0, vh * 0.5 - S.top);
        const itemsHeight = L - S.top;
        progress = itemsHeight > 0 ? Math.min(1, Math.max(0, scrolled / (itemsHeight * 0.8))) : 0;
      }
      this.style.setProperty('--rm-progress', progress.toFixed(4));

      if (this.manualReveal) {
        this.steps.forEach((step) => {
          const r = step.getBoundingClientRect();
          step.classList.toggle('is-visible', r.top < vh * 0.8 && r.bottom > vh * 0.2);
        });
      }
    }
  }

  customElements.define('elexa-roadmap', ElexaRoadmap);
})();
